/**
 * Customer Service - API functions for customer operations
 * These functions interact with the backend API
 */
import { apiFetch } from '../lib/apiFetch';

/**
 * Three-entity model:
 *   - 'individual' — person, no company affiliation. Has own billing.
 *   - 'company'    — business entity. Has own billing. May have Contacts.
 *   - 'contact'    — person who works for a Company. Billing rolls up to the
 *                    parent Company; tax/payment also inherited.
 *
 * Booqable doesn't model the company↔contact relationship — TWM owns it via
 * customers.company_id. customerKind is derived (not stored) from
 * legal_type + company_id:
 *   - company_id set            → 'contact'
 *   - legal_type='commercial'   → 'company'
 *   - otherwise                 → 'individual'
 */
export type CustomerKind = 'individual' | 'contact' | 'company';

/** One of a contact's company memberships. A contact may belong to more than
 *  one company (customer_company_links); exactly one is the primary. Ids are
 *  the company's id-of-record (Booqable id on the Booqable read path). */
export interface LinkedCompany {
  id: string;
  name: string;
  isPrimary: boolean;
  /** This contact is the billing contact FOR THIS company (Attn: on that
   *  company's invoices). At most one per company. Per-membership. */
  isBillingContact: boolean;
  /** This contact is the main point of contact FOR THIS company. At most one
   *  per company. Per-membership. (Distinct from isPrimary, which marks the
   *  contact's primary COMPANY.) */
  isMainContact: boolean;
  /** This contact receives THIS company's transactional email. Default
   *  opt-out; gated server-side in /emails/send. Per-membership. */
  emailOptIn: boolean;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  /** @deprecated Use customerKind instead. Kept for back-compat with older callers. */
  customerType?: string;
  customerKind?: CustomerKind;
  /** When customerKind === 'contact', the id of the PRIMARY company. */
  linkedCompanyId?: string | null;
  /** Display name of the primary Company (denormalized; for list rendering). */
  linkedCompanyName?: string;
  /** Full set of a contact's company memberships (primary + secondary).
   *  Populated by the single-record Booqable read path; the list path only
   *  carries the primary. Empty/undefined for non-contacts. */
  linkedCompanies?: LinkedCompany[];
  tags?: string[];
  phone?: string;
  /** Optional phone extension (companies/contacts). TWM-owned column on
   *  customers — distinct from `phone`, which is a Booqable property. */
  phoneExtension?: string | null;
  /** Payment methods offered on this customer's pay links. TWM-owned column.
   *  null/empty = card only (default); otherwise the explicit allowed set,
   *  e.g. ['card','ach'] or ['ach']. Elements: 'card', 'ach'. */
  allowedPaymentMethods?: string[] | null;
  /** Require a PO number before this customer's invoices can be finalized.
   *  TWM-owned boolean column (billed entities only). */
  poRequired?: boolean;
  /** PR services-withholding rate (fraction, e.g. 0.10 = 10%) auto-applied to
   *  this customer's invoices; null = not a withholding agent. TWM-owned
   *  numeric column (billed entities only). */
  prWithholdingRate?: number | null;
  /** Contact job title / role (e.g. "Operations Manager"). Contact-only.
   *  TWM-owned column on customers; Booqable has no equivalent. */
  title?: string | null;
  /** Free-text internal notes about this customer. TWM-owned column; distinct
   *  from the order-scoped "Customer Notes". */
  notes?: string | null;
  company?: string;
  company_id?: string | null;
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
  zipcode?: string;
  country?: string;
  // Billing name on the main_address property (NOT the customer's own name).
  // Booqable supports a billing name distinct from customer name — used for
  // commercial DBA, AP contacts, etc. Stored as first_name/last_name on the
  // main_address property attributes.
  address_first_name?: string;
  address_last_name?: string;
  tax_region_id?: string | null;
  hasPortalAccount?: boolean;
  /** Booqable's human-readable customer number (1, 2, 3…). Distinct from id. */
  number?: number;
  /** ISO timestamp when the customer was created. */
  created_at?: string;
  /** ISO timestamp of the customer's most recent order, or null if none. */
  latest_order_at?: string | null;
  /** Outstanding balance in cents (Booqable native unit). */
  balance_due_in_cents?: number;
  /** Company rows only: the designated main contact (person holding the
   *  main_contact role for this company). Undefined for individuals/contacts
   *  and for companies without one. */
  mainContactName?: string | null;
  mainContactEmail?: string | null;
  /** TWM uuid — populated when the customer is sourced from Supabase. */
  twm_id?: string | null;
  /** Booqable customer id — populated by both sources when available. */
  booqable_id?: string | null;
}

/**
 * Derive customerKind from raw legal_type + company_id. Centralized so all
 * call paths agree on the rule.
 */
export function deriveCustomerKind(
  legalType: string | undefined | null,
  companyId: string | undefined | null,
): CustomerKind {
  if (companyId) return 'contact';
  if (legalType === 'commercial' || legalType === 'Commercial' || legalType === 'Company') return 'company';
  return 'individual';
}

interface BooqableCustomerAttributes {
  name: string;
  email: string;
  legal_type?: string;
  tag_list?: string | string[];
  created_at?: string;
  tax_region_id?: string | null;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    region?: string;
    zipcode?: string;
    country?: string;
  };
}

interface BooqableCustomer {
  id: string;
  type: string;
  attributes: BooqableCustomerAttributes;
}

interface BooqableResponse {
  data: BooqableCustomer[];
}

/**
 * Map Booqable customer data to our Customer interface
 */
const mapBooqableCustomer = (customer: BooqableCustomer): Customer => {
  const attrs: BooqableCustomerAttributes = customer.attributes || {
    name: '',
    email: '',
  };

  // Parse tag_list - could be string, array, or null
  let tags: string[] = [];
  if (attrs.tag_list) {
    if (typeof attrs.tag_list === 'string') {
      tags = attrs.tag_list
        .split(',')
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag);
    } else if (Array.isArray(attrs.tag_list)) {
      tags = attrs.tag_list
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag);
    }
  }

  // Extract properties (phone, company_name, locations, etc.)
  const properties = (attrs as any).properties || {};

  // Backend attaches company_id and linked_company_name onto attributes via
  // _attach_company_link (Booqable doesn't model this; Supabase does).
  const linkedCompanyId = (attrs as any).company_id || null;
  const customerKind = deriveCustomerKind(attrs.legal_type, linkedCompanyId);

  return {
    id: customer.id,
    name: attrs.name || 'N/A',
    email: attrs.email || '',
    customerType: attrs.legal_type === 'commercial' ? 'Company' : 'Individual',
    customerKind,
    linkedCompanyId,
    linkedCompanyName: (attrs as any).linked_company_name || '',
    linkedCompanies: Array.isArray((attrs as any).linked_companies)
      ? (attrs as any).linked_companies.map((lc: any) => ({
          id: lc.id,
          name: lc.name || '',
          isPrimary: !!lc.is_primary,
          isBillingContact: !!lc.is_billing_contact,
          isMainContact: !!lc.main_contact,
          emailOptIn: !!lc.email_opt_in,
        }))
      : [],
    tags: tags,
    phoneExtension: (attrs as any).phone_extension || '',
    allowedPaymentMethods: (attrs as any).allowed_payment_methods || null,
    poRequired: !!(attrs as any).po_required,
    prWithholdingRate: typeof (attrs as any).pr_withholding_rate === 'number'
      ? (attrs as any).pr_withholding_rate : null,
    title: (attrs as any).title || '',
    notes: (attrs as any).notes || '',
    tax_region_id: attrs.tax_region_id || null,
    address1: attrs.address?.address1 || '',
    address2: attrs.address?.address2 || '',
    city: attrs.address?.city || '',
    region: attrs.address?.region || '',
    zipcode: attrs.address?.zipcode || '',
    country: attrs.address?.country || '',
    number: (attrs as any).number,
    created_at: (attrs as any).created_at,
    latest_order_at: (attrs as any).latest_order_at ?? null,
    balance_due_in_cents: (attrs as any).balance_due_in_cents ?? 0,
    twm_id: null,
    booqable_id: customer.id,
  };
};

/**
 * Minimal payload returned by /customers/lookup-by-email — fields the dedup
 * banner + hover popover need, nothing more. Distinct from `Customer` so the
 * caller can't accidentally treat a lookup hit as a fully populated record.
 */
export interface EmailDedupMatch {
  id: string;
  booqable_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  company_id: string | null;
  legal_type: string | null;
  created_at: string | null;
  latest_order_at: string | null;
}

/**
 * Lookup existing customers whose normalized email (lowercase + +alias
 * stripped) matches the input. Returns [] when the input has no match, isn't
 * a full email yet, or fails — never throws — so the Add-drawer banner stays
 * silent rather than flashing an error while the user is typing.
 */
export const lookupCustomersByEmail = async (
  email: string
): Promise<EmailDedupMatch[]> => {
  const trimmed = email.trim();
  if (!trimmed || !trimmed.includes('@')) return [];
  try {
    const response = await apiFetch(
      `/customers/lookup-by-email?email=${encodeURIComponent(trimmed)}`
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.data) ? data.data : [];
  } catch {
    return [];
  }
};

/**
 * A customer parked for manual Auth.Net link review — surfaced when the link
 * sweep flagged `authnet_link_status='ambiguous'` (a profile exists for the
 * email but AN couldn't disambiguate). Backs the Settings → Auth.Net linking
 * review list. See #278.
 */
export interface AuthnetReviewCustomer {
  id: string;
  name: string;
  email: string;
  /** ISO timestamp the sweep last flagged this row, or null. */
  authnet_synced_at: string | null;
}

/**
 * Fetch customers awaiting manual Auth.Net link review. Returns [] on any
 * failure so the list renders empty rather than throwing.
 */
export const fetchAuthnetReviewCustomers = async (): Promise<AuthnetReviewCustomer[]> => {
  const response = await apiFetch(`/customers/authnet-review`);
  if (!response.ok) {
    throw new Error(`Failed to fetch Auth.Net review list: ${response.statusText}`);
  }
  const data = await response.json();
  return Array.isArray(data.data) ? data.data : [];
};

/**
 * Map a flat Supabase customer record to our Customer interface
 */
const mapDbCustomer = (c: any): Customer => {
  const linkedCompanyId = c.company_id || null;
  return {
    id: c.id,
    name: c.name || 'N/A',
    email: c.email || '',
    customerType: c.legal_type === 'commercial' ? 'Company' : 'Individual',
    customerKind: deriveCustomerKind(c.legal_type, linkedCompanyId),
    linkedCompanyId,
    linkedCompanyName: c.linked_company_name || '',
    tags: c.tags || [],
    phone: c.phone || '',
    phoneExtension: c.phone_extension || '',
    allowedPaymentMethods: c.allowed_payment_methods || null,
    poRequired: !!c.po_required,
    prWithholdingRate: typeof c.pr_withholding_rate === 'number' ? c.pr_withholding_rate : null,
    title: c.title || '',
    notes: c.notes || '',
    company: c.company_name || '',
    company_id: linkedCompanyId,
    address1: c.address?.address1 || '',
    address2: c.address?.address2 || '',
    city: c.address?.city || '',
    region: c.address?.region || '',
    zipcode: c.address?.zipcode || '',
    country: c.address?.country || '',
    tax_region_id: c.tax_region_id || null,
    hasPortalAccount: c.has_portal_account ?? false,
    number: c.number ?? undefined,
    created_at: c.created_at ?? undefined,
    latest_order_at: c.latest_order_at ?? null,
    balance_due_in_cents: c.balance_due_in_cents ?? 0,
    mainContactName: c.main_contact_name ?? null,
    mainContactEmail: c.main_contact_email ?? null,
    twm_id: c.id,
    booqable_id: c.booqable_id || null,
  };
};

/**
 * Raw JSON:API envelope from /booqable/get-customers — `data` is the customer
 * array, `included` carries properties (phone/company), addresses, and
 * tax_regions. Both are needed to populate the customer drawer without a
 * second network round-trip.
 */
export interface BooqableCustomerListResponse {
  data: any[];
  included: any[];
}

// Booqable caps a page at 500. We page through ALL pages rather than fetching
// only page 1, so the admin list stops silently truncating past 500 customers.
const CUSTOMER_PAGE_SIZE = 500;
// Safety cap so a bad meta payload can't spin into an unbounded fetch loop.
const MAX_CUSTOMER_PAGES = 40;

/**
 * Resolve how many pages to fetch from Booqable's `meta`. We CANNOT infer "last
 * page" from a short page: `_apply_env_visibility` drops cross-env customers
 * after Booqable returns the full 500, so a page can come back short while more
 * pages remain. `meta` reflects the unfiltered Booqable total, so it's the
 * reliable driver. Falls back to 1 page if meta is absent.
 */
const resolveTotalPages = (meta: any): number => {
  if (!meta) return 1;
  if (typeof meta.total_pages === 'number' && meta.total_pages > 0) {
    return Math.min(meta.total_pages, MAX_CUSTOMER_PAGES);
  }
  if (typeof meta.total_count === 'number' && meta.total_count > 0) {
    return Math.min(Math.ceil(meta.total_count / CUSTOMER_PAGE_SIZE), MAX_CUSTOMER_PAGES);
  }
  return 1;
};

const fetchCustomerPage = async (page: number): Promise<{ data: any[]; included: any[]; meta: any }> => {
  const response = await apiFetch(`/booqable/get-customers?per_page=${CUSTOMER_PAGE_SIZE}&page=${page}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch customers (page ${page}): ${response.statusText}`);
  }
  const data = await response.json();
  return { data: data.data || [], included: data.included || [], meta: data.meta };
};

/**
 * Fetch the raw Booqable customer list with relations included, paging through
 * every page. Useful when the caller wants to pre-warm React Query detail caches
 * alongside the list display.
 */
export const fetchCustomersRaw = async (): Promise<BooqableCustomerListResponse> => {
  const first = await fetchCustomerPage(1);
  const allData = [...first.data];
  const allIncluded = [...first.included];

  const totalPages = resolveTotalPages(first.meta);
  for (let page = 2; page <= totalPages; page++) {
    const next = await fetchCustomerPage(page);
    allData.push(...next.data);
    allIncluded.push(...next.included);
  }

  return { data: allData, included: allIncluded };
};

/**
 * Map a raw Booqable customer list response to flat Customer objects (alphabetized).
 * Archived customers are filtered out — Booqable's default list still returns
 * them, and "delete" in our admin is really an archive.
 */
export const mapCustomersList = (raw: BooqableCustomerListResponse): Customer[] => {
  const mapped = raw.data
    .filter((c) => {
      const a: any = c.attributes || {};
      return !(a.archived === true || a.archived_at);
    })
    .map(mapBooqableCustomer);
  mapped.sort((a, b) => a.name.localeCompare(b.name));
  return mapped;
};

/**
 * Fetch all customers — from Supabase (synced copy) or Booqable (live source of truth)
 */
export const fetchCustomers = async (useLocal = false): Promise<Customer[]> => {
  if (useLocal) {
    const response = await apiFetch(`/customers`);
    if (!response.ok) {
      throw new Error(`Failed to fetch customers: ${response.statusText}`);
    }
    const data = await response.json();
    const mapped = (data.data || [])
      .filter((c: any) => !(c.archived === true || c.archived_at))
      .map(mapDbCustomer);
    mapped.sort((a, b) => a.name.localeCompare(b.name));
    console.log(`✓ Fetched ${mapped.length} customers from Supabase`);
    return mapped;
  }

  const raw = await fetchCustomersRaw();
  const mapped = mapCustomersList(raw);
  console.log(`✓ Fetched ${mapped.length} customers from Booqable (live)`);
  return mapped;
};

/**
 * Slice a Booqable customer list response down to a single customer's relations.
 * Filters `included` to just the properties / addresses / tax_region that belong
 * to the given customer so the detail cache entry is correctly scoped.
 */
export const sliceCustomerFromList = (
  customerId: string,
  raw: BooqableCustomerListResponse
): { data: any; included: any[] } | null => {
  const customer = raw.data.find((c) => c.id === customerId);
  if (!customer) return null;

  const included = raw.included || [];

  // Properties are linked via owner_id / owner_type in their attributes.
  const properties = included.filter(
    (i) =>
      i.type === 'properties' &&
      i.attributes?.owner_id === customerId &&
      i.attributes?.owner_type === 'customers'
  );

  // Addresses link back via either relationships.customer.data.id (preferred)
  // or attributes.customer_id depending on Booqable response shape.
  const addresses = included.filter(
    (i) =>
      i.type === 'addresses' &&
      (i.relationships?.customer?.data?.id === customerId ||
        i.attributes?.customer_id === customerId)
  );

  // tax_region is referenced by id from the customer's relationships block.
  const taxRegionId = customer.relationships?.tax_region?.data?.id;
  const taxRegion = taxRegionId
    ? included.filter((i) => i.type === 'tax_regions' && i.id === taxRegionId)
    : [];

  return {
    data: customer,
    included: [...properties, ...addresses, ...taxRegion],
  };
};

/**
 * Fetch a single customer by ID
 */
export const fetchCustomerById = async (customerId: string): Promise<Customer> => {
  const response = await apiFetch(
    `/booqable/customers/${customerId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch customer: ${response.statusText}`);
  }

  const data = await response.json();
  return mapBooqableCustomer(data.data);
};

/**
 * Create a new customer
 */
export const createCustomer = async (customerData: Partial<Customer>): Promise<Customer> => {
  // Prefer customerKind ('individual' | 'contact' | 'company') over the
  // legacy customerType string. Either maps cleanly to legal_type.
  const legalType = customerData.customerKind === 'company'
    ? 'commercial'
    : customerData.customerType === 'Company'
      ? 'commercial'
      : 'person';

  const response = await apiFetch(`/booqable/create/customers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        type: 'customers',
        attributes: {
          name: customerData.name,
          email: customerData.email,
          legal_type: legalType,
          tag_list: customerData.tags,
        },
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create customer: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✓ Customer created:', data.data.id);
  return mapBooqableCustomer(data.data);
};

/**
 * Update an existing customer
 * Handles both attributes and properties (phone, company)
 */
export const updateCustomer = async (
  customerId: string,
  customerData: Partial<Customer>
): Promise<Customer> => {
  // Step 1: Update customer attributes (name, email, legal_type, tags only)
  // Note: Address is handled separately via addresses endpoint
  const attributes: any = {};

  // Only include fields that have actual values
  if (customerData.name) {
    attributes.name = customerData.name;
  }

  // Pass null on empty so the user can clear the email; truthy-only check
  // would silently drop the field and leave the prior value in Booqable.
  if (customerData.email !== undefined) {
    attributes.email = customerData.email || null;
  }

  // customerKind is the new source of truth; map to Booqable's legal_type.
  // 'company' → commercial; 'individual' or 'contact' → person.
  if (customerData.customerKind) {
    attributes.legal_type = customerData.customerKind === 'company' ? 'commercial' : 'person';
  } else if (customerData.customerType) {
    attributes.legal_type = customerData.customerType === 'Company' ? 'commercial' : 'person';
  }

  // Tags: Send as array if defined
  if (customerData.tags !== undefined) {
    attributes.tag_list = customerData.tags;
  }

  // Tax region ID: Send if defined (can be null to clear)
  if (customerData.tax_region_id !== undefined) {
    attributes.tax_region_id = customerData.tax_region_id;
  }

  // Phone and company are Booqable Properties (separate from customer
  // attributes), upserted by the backend at /booqable/customers/<id>.
  // Send them only when defined; empty string → backend deletes the
  // existing property. Skipping a key leaves it untouched.
  const properties: Record<string, string> = {};
  if (customerData.phone !== undefined) properties.phone = customerData.phone || '';
  if (customerData.company !== undefined) properties.company = customerData.company || '';

  // Address is stored as a Booqable property (identifier='main_address').
  // Backend upserts via /properties; '' clears a field, undefined leaves it
  // untouched. address_first_name / address_last_name are the billing name
  // fields on that same property (distinct from customer.name).
  const address: Record<string, string> = {};
  if (customerData.address1 !== undefined) address.address1 = customerData.address1 || '';
  if (customerData.address2 !== undefined) address.address2 = customerData.address2 || '';
  if (customerData.city !== undefined) address.city = customerData.city || '';
  if (customerData.region !== undefined) address.region = customerData.region || '';
  if (customerData.zipcode !== undefined) address.zipcode = customerData.zipcode || '';
  if (customerData.country !== undefined) address.country = customerData.country || '';
  if (customerData.address_first_name !== undefined) address.first_name = customerData.address_first_name || '';
  if (customerData.address_last_name !== undefined) address.last_name = customerData.address_last_name || '';

  // phone_extension is a TWM-owned column (not a Booqable field) — sent as a
  // top-level body key the backend mirrors into Supabase. Send only when
  // defined; empty string clears it, undefined leaves it untouched.
  const sendPhoneExtension = customerData.phoneExtension !== undefined;

  // allowed_payment_methods: likewise a TWM-owned column, sent as a top-level
  // array key. Send only when defined; the backend sanitizes to {card,ach}
  // and stores [] as NULL (card-only default).
  const sendAllowedMethods = customerData.allowedPaymentMethods !== undefined;

  // po_required is a TWM-owned boolean column — same top-level, presence-
  // signaled pattern. Send only when defined; the backend coerces to bool and
  // mirrors. (email_opt_in / is_billing_contact are per-membership now — set
  // via setMembershipFlags, not this customer PATCH.)
  const sendPoRequired = customerData.poRequired !== undefined;

  // pr_withholding_rate is a TWM-owned numeric column — same top-level,
  // presence-signaled pattern. Send only when defined; null clears it.
  const sendPrWithholding = customerData.prWithholdingRate !== undefined;

  // title (contact-only) and notes are TWM-owned columns — same top-level,
  // presence-signaled pattern. Send only when defined; empty string clears.
  const sendTitle = customerData.title !== undefined;
  const sendNotes = customerData.notes !== undefined;

  // Skip the PATCH when nothing maps to a backend-handled field — backend
  // would 400 with "Nothing to update". This happens when the only change is
  // linkedCompanyId, which is a TWM-side relationship handled by a separate
  // PUT below.
  const hasBooqableUpdates =
    Object.keys(attributes).length > 0 ||
    Object.keys(properties).length > 0 ||
    Object.keys(address).length > 0 ||
    sendPhoneExtension ||
    sendAllowedMethods ||
    sendPoRequired ||
    sendPrWithholding ||
    sendTitle ||
    sendNotes;

  let booqableData: any = null;

  if (hasBooqableUpdates) {
    const updatePayload = {
      data: {
        type: 'customers',
        id: customerId,
        attributes: attributes,
      },
      ...(Object.keys(properties).length > 0 ? { properties } : {}),
      ...(Object.keys(address).length > 0 ? { address } : {}),
      ...(sendPhoneExtension ? { phone_extension: customerData.phoneExtension || '' } : {}),
      ...(sendAllowedMethods ? { allowed_payment_methods: customerData.allowedPaymentMethods || [] } : {}),
      ...(sendPoRequired ? { po_required: !!customerData.poRequired } : {}),
      ...(sendPrWithholding ? { pr_withholding_rate: customerData.prWithholdingRate ?? null } : {}),
      ...(sendTitle ? { title: customerData.title || '' } : {}),
      ...(sendNotes ? { notes: customerData.notes || '' } : {}),
    };

    console.log('Update payload:', JSON.stringify(updatePayload, null, 2));

    // Hits the TWM-side endpoint with dual-ID resolution (TWM customers.id OR
    // booqable_id). Replaces the prior /booqable-proxy passthrough that required
    // the caller to know the booqable_id and bypassed local mirroring.
    const response = await apiFetch(
      `/booqable/customers/${customerId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('=== BOOQABLE API ERROR ===');
      console.error('Status:', response.status, response.statusText);
      console.error('Full error response:', JSON.stringify(errorData, null, 2));
      console.error('Payload sent:', JSON.stringify(updatePayload, null, 2));

      // Extract error details. Backend wraps Booqable's response inside a
      // `details` field; check both shapes (top-level for direct Booqable
      // errors, details.* for the wrapped form).
      let errorMessage = response.statusText;
      const innerErrors =
        (errorData.errors as any[] | undefined) ||
        (errorData.details?.errors as any[] | undefined);
      if (innerErrors && innerErrors.length > 0) {
        const errorDetails = innerErrors.map((e: any) => {
          const field = e.source?.pointer || 'unknown';
          const message = e.title || e.detail || 'Unknown error';
          return `${field}: ${message}`;
        });
        errorMessage = errorDetails.join(', ');
        console.error('Parsed errors:', errorDetails);
      } else if (errorData.details?.error) {
        errorMessage = errorData.details.error;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }

      throw new Error(`Failed to update customer: ${errorMessage}`);
    }

    const data = await response.json();
    console.log('✓ Customer updated:', customerId);

    // The backend route at PATCH /booqable/customers/<id> already upserts
    // properties (phone/company) and the primary address from the same payload
    // we sent above (`properties` and `address` keys on the body). No additional
    // round trips needed here — earlier client-side property/address calls were
    // redundant and added 5 sequential round trips per save.

    // Partial-failure surfacing: backend returns 200 with a `warnings` array
    // when the customer attribute PATCH succeeded but a sub-operation
    // (property/address upsert) failed. We throw so the mutation's onError
    // path can show the message + invalidate caches to drop optimistic state.
    if (Array.isArray(data.warnings) && data.warnings.length > 0) {
      throw new Error(`Some changes didn't save: ${data.warnings.join('; ')}`);
    }

    booqableData = data.data;
  }

  // Contact↔Company link is a TWM-side relationship — Booqable doesn't model
  // it. Send a separate PUT to /customers/<id>/company when the link changes.
  // Caller signals "no change" by leaving linkedCompanyId undefined; explicit
  // null clears the link.
  if (customerData.linkedCompanyId !== undefined) {
    try {
      await setCustomerCompany(customerId, customerData.linkedCompanyId);
    } catch (linkErr) {
      console.error('Failed to update company link:', linkErr);
      throw new Error(
        `Customer saved but linking to company failed: ${
          linkErr instanceof Error ? linkErr.message : 'Unknown error'
        }`,
      );
    }
  }

  // When the Booqable PATCH didn't run (linkedCompanyId-only change), we
  // don't have a fresh server payload to map. Callers (updateMutation) read
  // `variables` for cache patches, not this return value, so a best-effort
  // shape is fine.
  return booqableData
    ? mapBooqableCustomer(booqableData)
    : ({ id: customerId, ...customerData } as Customer);
};

/**
 * Fetch all customers whose customerKind is 'company'. Backend returns the
 * full customer list; we filter clientside on derived kind. Used by the
 * Linked Company picker on the Contact form.
 */
export const fetchCompanies = async (): Promise<Customer[]> => {
  const customers = await fetchCustomers(true);
  return customers.filter((c) => c.customerKind === 'company');
};

/**
 * Fetch all contacts for a company customer
 */
export const fetchCustomerContacts = async (companyId: string): Promise<Customer[]> => {
  const response = await apiFetch(`/customers/${companyId}/contacts`);
  if (!response.ok) {
    throw new Error(`Failed to fetch contacts: ${response.statusText}`);
  }
  const data = await response.json();
  return (data.data || []).map(mapDbCustomer);
};

/**
 * Set (or clear) a contact's PRIMARY company. Passing null removes ALL of the
 * contact's company memberships. Used by the create-contact flow and the
 * primary-company picker; use add/removeCustomerCompany for additional
 * memberships.
 */
export const setCustomerCompany = async (
  contactId: string,
  companyId: string | null
): Promise<void> => {
  const response = await apiFetch(`/customers/${contactId}/company`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ company_id: companyId }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update company association: ${response.statusText}`);
  }
};

/**
 * Add a company membership to a contact. isPrimary omitted → becomes primary
 * only if the contact has no primary yet.
 */
export const addCustomerCompany = async (
  contactId: string,
  companyId: string,
  isPrimary?: boolean
): Promise<void> => {
  const response = await apiFetch(`/customers/${contactId}/companies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ company_id: companyId, ...(isPrimary !== undefined ? { is_primary: isPrimary } : {}) }),
  });
  if (!response.ok) {
    throw new Error(`Failed to add company: ${response.statusText}`);
  }
};

/**
 * Remove one of a contact's company memberships. If it was the primary and
 * others remain, the backend promotes the oldest survivor.
 */
export const removeCustomerCompany = async (
  contactId: string,
  companyId: string
): Promise<void> => {
  const response = await apiFetch(`/customers/${contactId}/companies/${companyId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to remove company: ${response.statusText}`);
  }
};

/**
 * Designate an existing membership as the contact's primary company.
 */
export const setPrimaryCompany = async (
  contactId: string,
  companyId: string
): Promise<void> => {
  const response = await apiFetch(`/customers/${contactId}/companies/${companyId}/primary`, {
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error(`Failed to set primary company: ${response.statusText}`);
  }
};

/**
 * Set per-membership flags on a contact<->company link. All are per-membership
 * (a contact may hold a role / be opted in for company A but not B).
 * is_billing_contact and main_contact are one-per-company roles: setting one
 * true demotes any sibling holder on the same company (enforced server-side).
 * Setting is_billing_contact=true also opts the contact into email. Only the
 * provided flags are changed.
 */
export const setMembershipFlags = async (
  contactId: string,
  companyId: string,
  flags: { is_billing_contact?: boolean; email_opt_in?: boolean; main_contact?: boolean }
): Promise<void> => {
  const response = await apiFetch(
    `/customers/${contactId}/companies/${companyId}/flags`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(flags),
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to update membership flags: ${response.statusText}`);
  }
};

/**
 * Thrown when DELETE /booqable/customers/<id> returns 409 because the
 * customer is a Company with active Contacts. The caller should open the
 * conflict-resolution modal instead of surfacing a generic error.
 */
export class CompanyHasContactsError extends Error {
  contactCount: number;
  companyId: string;
  constructor(contactCount: number, companyId: string) {
    super(`Cannot delete a company with ${contactCount} active contact(s)`);
    this.name = 'CompanyHasContactsError';
    this.contactCount = contactCount;
    this.companyId = companyId;
  }
}

/**
 * Delete a customer. Pass `{ cascade: true }` to also archive any active
 * Contacts linked to a Company (the backend handles the loop atomically).
 */
export const deleteCustomer = async (
  customerId: string,
  options: { cascade?: boolean } = {}
): Promise<void> => {
  const qs = options.cascade ? '?cascade=true' : '';
  const url = `/booqable/customers/${customerId}${qs}`;
  console.log('[delete-customer] fetching:', url);
  const response = await apiFetch(url, { method: 'DELETE' });

  if (!response.ok) {
    if (response.status === 409) {
      // Backend signals "company has contacts" — parse the structured payload
      // so the caller can route into the conflict-resolution modal.
      try {
        const body = await response.json();
        if (typeof body?.contact_count === 'number' && typeof body?.company_id === 'string') {
          throw new CompanyHasContactsError(body.contact_count, body.company_id);
        }
      } catch (e) {
        if (e instanceof CompanyHasContactsError) throw e;
        // fall through to generic error
      }
    }
    throw new Error(`Failed to delete customer: ${response.statusText}`);
  }

  console.log('✓ Customer deleted:', customerId);
};

/**
 * Delete multiple customers. Returns the per-id outcome so the caller can
 * surface a "skipped N companies" toast for the 409s without aborting the
 * batch. Resolved values: 'ok' (deleted), 'has_contacts' (409 — kept),
 * 'error' (other failure).
 */
export type BulkDeleteOutcome = 'ok' | 'has_contacts' | 'error';
export const deleteCustomers = async (
  customerIds: string[]
): Promise<Array<{ id: string; outcome: BulkDeleteOutcome }>> => {
  const results = await Promise.all(
    customerIds.map(async (id): Promise<{ id: string; outcome: BulkDeleteOutcome }> => {
      try {
        await deleteCustomer(id);
        return { id, outcome: 'ok' };
      } catch (err) {
        if (err instanceof CompanyHasContactsError) return { id, outcome: 'has_contacts' };
        return { id, outcome: 'error' };
      }
    })
  );
  const okCount = results.filter((r) => r.outcome === 'ok').length;
  console.log(`✓ Deleted ${okCount}/${customerIds.length} customers`);
  return results;
};


/**
 * Bulk update customer tags
 */
export const bulkUpdateTags = async (
  customerIds: string[],
  tags: string[]
): Promise<void> => {
  await Promise.all(
    customerIds.map((id) =>
      updateCustomer(id, { tags })
    )
  );
  console.log(`✓ Updated tags for ${customerIds.length} customers`);
};
